import { Employee, WeeklySchedule, EmployeeStatusEnum, PlanningMetrics, TimeSlot } from './types';
import { generateUniqueId, calculateWeeklyHours, calculateHoursFromTimeSlots } from './utils';

// Mock data for employees
export const mockEmployees: Employee[] = [
    {
        id: '1',
        firstName: 'Marie',
        lastName: 'Dupont',
        position: 'Vendeuse',
        avatar: undefined
    },
    {
        id: '2',
        firstName: 'Jean',
        lastName: 'Martin',
        position: 'Manager',
        avatar: undefined
    },
    {
        id: '3',
        firstName: 'Sophie',
        lastName: 'Bernard',
        position: 'Caissière',
        avatar: undefined
    },
    {
        id: '4',
        firstName: 'Pierre',
        lastName: 'Leroy',
        position: 'Vendeur',
        avatar: undefined
    },
    {
        id: '5',
        firstName: 'Emma',
        lastName: 'Moreau',
        position: 'Responsable',
        avatar: undefined
    }
];

// Helper function to get current week dates
export const getCurrentWeekDates = (date: Date = new Date()): Date[] => {
    const currentDate = new Date(date);
    const monday = new Date(currentDate);
    const dayOfWeek = currentDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    monday.setDate(currentDate.getDate() + daysToMonday);
    
    const weekDates: Date[] = [];
    for (let i = 0; i < 7; i++) {
        const day = new Date(monday);
        day.setDate(monday.getDate() + i);
        weekDates.push(day);
    }
    return weekDates;
};

// Generate random time slots for a working day
const generateRandomTimeSlots = (): TimeSlot[] => {
    const random = Math.random();
    
    if (random < 0.6) {
        // Single time slot (most common)
        return [{
            id: generateUniqueId(),
            startTime: '09:00',
            endTime: '18:00'
        }];
    } else if (random < 0.85) {
        // Two time slots with lunch break
        return [
            {
                id: generateUniqueId(),
                startTime: '09:00',
                endTime: '12:00'
            },
            {
                id: generateUniqueId(),
                startTime: '13:00',
                endTime: '17:00'
            }
        ];
    } else {
        // Three time slots (split day)
        return [
            {
                id: generateUniqueId(),
                startTime: '08:00',
                endTime: '11:00'
            },
            {
                id: generateUniqueId(),
                startTime: '14:00',
                endTime: '17:00'
            },
            {
                id: generateUniqueId(),
                startTime: '19:00',
                endTime: '21:00'
            }
        ];
    }
};
export const generateMockWeeklySchedules = (weekStartDate: Date): WeeklySchedule[] => {
    const weekDates = getCurrentWeekDates(weekStartDate);
    
    return mockEmployees.map(employee => {
        const days: Record<string, any> = {};
        
        weekDates.forEach((date, index) => {
            const dateKey = date.toISOString().split('T')[0];
            
            // Generate random status for each day
            let status: EmployeeStatusEnum;
            const random = Math.random();
            
            // Weekend logic
            if (index === 5 || index === 6) { // Saturday and Sunday
                if (random < 0.2) {
                    status = EmployeeStatusEnum.PRESENT;
                } else if (random < 0.3) {
                    status = EmployeeStatusEnum.SHOP_CLOSED;
                } else if (random < 0.5) {
                    status = EmployeeStatusEnum.ABSENT;
                } else {
                    status = EmployeeStatusEnum.DEFAULT;
                }
            } else {
                // Weekdays
                if (random < 0.4) {
                    status = EmployeeStatusEnum.PRESENT;
                } else if (random < 0.55) {
                    status = EmployeeStatusEnum.ABSENT;
                } else if (random < 0.65) {
                    status = EmployeeStatusEnum.VACATION;
                } else if (random < 0.75) {
                    status = EmployeeStatusEnum.HOLIDAY;
                } else {
                    status = EmployeeStatusEnum.DEFAULT;
                }
            }
            
            days[dateKey] = {
                date,
                status,
                timeSlots: status === EmployeeStatusEnum.PRESENT ? generateRandomTimeSlots() : [],
                notes: ''
            };
        });
        
        return {
            employeeId: employee.id,
            weekStartDate,
            days
        };
    });
};

// Generate mock monthly schedules
export const generateMockMonthlySchedules = (currentMonth: Date): WeeklySchedule[] => {
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    
    // Find the first Monday before or on the start of month
    const startDate = new Date(startOfMonth);
    const dayOfWeek = startDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    startDate.setDate(startDate.getDate() + daysToMonday);
    
    // Generate all days for the month view (6 weeks)
    const allDays: Date[] = [];
    for (let i = 0; i < 42; i++) {
        const day = new Date(startDate);
        day.setDate(startDate.getDate() + i);
        allDays.push(day);
    }
    
    return mockEmployees.map(employee => {
        const days: Record<string, any> = {};
        
        allDays.forEach(date => {
            const dateKey = date.toISOString().split('T')[0];
            const dayOfWeek = date.getDay();
            
            // Generate random status for each day
            let status: EmployeeStatusEnum;
            const random = Math.random();
            
            // Weekend logic
            if (dayOfWeek === 0 || dayOfWeek === 6) { // Saturday and Sunday
                if (random < 0.2) {
                    status = EmployeeStatusEnum.PRESENT;
                } else if (random < 0.3) {
                    status = EmployeeStatusEnum.SHOP_CLOSED;
                } else if (random < 0.5) {
                    status = EmployeeStatusEnum.ABSENT;
                } else {
                    status = EmployeeStatusEnum.DEFAULT;
                }
            } else {
                // Weekdays
                if (random < 0.4) {
                    status = EmployeeStatusEnum.PRESENT;
                } else if (random < 0.55) {
                    status = EmployeeStatusEnum.ABSENT;
                } else if (random < 0.65) {
                    status = EmployeeStatusEnum.VACATION;
                } else if (random < 0.75) {
                    status = EmployeeStatusEnum.HOLIDAY;
                } else {
                    status = EmployeeStatusEnum.DEFAULT;
                }
            }
            
            days[dateKey] = {
                date,
                status,
                timeSlots: status === EmployeeStatusEnum.PRESENT ? generateRandomTimeSlots() : [],
                notes: ''
            };
        });
        
        return {
            employeeId: employee.id,
            weekStartDate: startDate,
            days
        };
    });
};
export const generateMockMetrics = (schedules: WeeklySchedule[]): PlanningMetrics => {
    const today = new Date().toISOString().split('T')[0];
    const totalEmployees = mockEmployees.length;
    
    let presentToday = 0;
    let absentToday = 0;
    let onVacationToday = 0;
    let totalPresenceWeek = 0;
    let totalDaysWeek = 0;
    
    schedules.forEach(schedule => {
        Object.entries(schedule.days).forEach(([dateKey, daySchedule]) => {
            if (dateKey === today) {
                switch (daySchedule.status) {
                    case EmployeeStatusEnum.PRESENT:
                        presentToday++;
                        break;
                    case EmployeeStatusEnum.ABSENT:
                        absentToday++;
                        break;
                    case EmployeeStatusEnum.VACATION:
                        onVacationToday++;
                        break;
                }
            }
            
            // Count for weekly presence rate
            if (daySchedule.status === EmployeeStatusEnum.PRESENT) {
                totalPresenceWeek++;
            }
            totalDaysWeek++;
        });
    });
    
    const weeklyPresenceRate = totalDaysWeek > 0 ? (totalPresenceWeek / totalDaysWeek) * 100 : 0;
    
    // Calculate total weekly hours
    const totalWeeklyHours = schedules.reduce((total, schedule) => {
        return total + calculateWeeklyHours(schedule);
    }, 0);
    
    const averageHoursPerEmployee = totalEmployees > 0 ? totalWeeklyHours / totalEmployees : 0;
    
    return {
        totalEmployees,
        presentToday,
        absentToday,
        onVacationToday,
        weeklyPresenceRate: Math.round(weeklyPresenceRate),
        monthlyPresenceRate: Math.round(weeklyPresenceRate * 0.9), // Mock monthly rate
        totalWeeklyHours: Math.round(totalWeeklyHours * 10) / 10, // Round to 1 decimal
        averageHoursPerEmployee: Math.round(averageHoursPerEmployee * 10) / 10
    };
};